import React, { useState } from 'react'
import { useParams, Navigate, Link } from 'react-router-dom'
import { ArrowLeft, Edit, Play, Plus, BarChart3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { useTestSets } from '@/hooks/useTestSets'
import { TestCaseManager } from '@/components/evaluation/TestCaseManager'
import { EvaluationRunner } from '@/components/evaluation/EvaluationRunner'
import { EvaluationHistory } from '@/components/evaluation/EvaluationHistory'
import { EditTestSetModal } from '@/components/evaluation/EditTestSetModal'
import { PROTECTED_ROUTES } from '@/constants/routes'

const TestSetDetailPage: React.FC = () => {
  const { testSetId } = useParams<{ testSetId: string }>()
  const { currentTestSet, loading, error, refreshCurrentTestSet } = useTestSets(testSetId)
  const [showEditModal, setShowEditModal] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')

  if (!testSetId) {
    return <Navigate to={PROTECTED_ROUTES.EVALUATIONS} replace />
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-8">
          <p className="text-destructive mb-4">{error}</p>
          <Link to={PROTECTED_ROUTES.EVALUATIONS}>
            <Button variant="outline">Back to Evaluations</Button>
          </Link>
        </div>
      </div>
    )
  }

  if (!currentTestSet) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    )
  }

  const testSet = currentTestSet

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={PROTECTED_ROUTES.EVALUATIONS}>
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{testSet.name}</h1>
            {testSet.description && (
              <p className="text-muted-foreground mt-1">{testSet.description}</p>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          <Link to={`${PROTECTED_ROUTES.EVALUATIONS}/${testSetId}/results`}>
            <Button variant="outline" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Results
            </Button>
          </Link>
          <Button 
            variant="outline" 
            onClick={() => setShowEditModal(true)}
            className="gap-2"
          >
            <Edit className="h-4 w-4" />
            Edit
          </Button>
          <Button className="gap-2" onClick={() => setActiveTab('run')}>
            <Play className="h-4 w-4" />
            Run Evaluation
          </Button>
        </div>
      </div>

      {/* Test Set Info */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Test Set Information</CardTitle>
              <CardDescription>Configuration and statistics</CardDescription>
            </div>
            <div className="flex gap-2">
              <Badge variant="secondary">{testSet.model}</Badge>
              <Badge variant="outline">Temp: {testSet.temperature}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground">Test Cases</div>
              <div className="text-2xl font-bold">{testSet._count.testCases}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Total Runs</div>
              <div className="text-2xl font-bold">{testSet._count.evaluationRuns}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Last Run</div>
              <div className="text-2xl font-bold">
                {testSet.latestRun ? 
                  new Date(testSet.latestRun.startedAt).toLocaleDateString() : 
                  'Never'
                }
              </div>
            </div>
          </div>

          {testSet.latestRun && (
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Latest Run Status</div>
                  <Badge 
                    variant={testSet.latestRun.status === 'COMPLETED' ? 'default' : 
                            testSet.latestRun.status === 'FAILED' ? 'destructive' : 'secondary'}
                  >
                    {testSet.latestRun.status}
                  </Badge>
                </div>
                {testSet.latestRun.averageScore !== undefined && (
                  <div className="text-right">
                    <div className="text-sm font-medium text-muted-foreground">Average Score</div>
                    <div className="text-2xl font-bold">{testSet.latestRun.averageScore.toFixed(1)}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="cases">Test Cases</TabsTrigger>
          <TabsTrigger value="run">Run Evaluation</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>System Prompt</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-muted p-4 rounded-lg">
                <pre className="whitespace-pre-wrap text-sm">{testSet.systemPrompt}</pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cases" className="space-y-4">
          <TestCaseManager testSetId={testSetId} testSet={testSet} onRefresh={refreshCurrentTestSet} />
        </TabsContent>

        <TabsContent value="run" className="space-y-4">
          <EvaluationRunner 
            testSetId={testSetId} 
            testSet={testSet}
            onRunComplete={() => {
              // Refresh the test set data after a run completes
              refreshCurrentTestSet()
            }}
          />
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <EvaluationHistory testSetId={testSetId} />
        </TabsContent>
      </Tabs>

      {/* Edit Modal */}
      {showEditModal && (
        <EditTestSetModal
          testSet={testSet}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  )
}

export default TestSetDetailPage